import { useCallback } from "react";
import { useProfileDraft } from "@/context/ProfileDraftContext";
import type { SectionType } from "@profolio/validators";
import {
  SortableSectionItem,
  SectionDragOverlayItem,
} from "@/components/sections/SortableSectionItem";
import { SECTION_METADATA, getSectionItemCount } from "@/components/content/types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Palette, LayoutTemplate, Type, FileText, PanelBottom, Plus, PanelLeftClose, PanelLeftOpen, Sparkles, Sliders, Layers } from "lucide-react";
import { useState } from "react";
import type { SectionData } from "@profolio/templates";
import { AddSectionModal } from "@/components/content/AddSectionModal";
import { DeleteSectionDialog } from "@/components/content/DeleteSectionDialog";
import { createDefaultSectionContent } from "@/components/content/types";
import { Button } from "@/components/ui/button";
interface PortfolioOutlineProps {
  onNavigate?: () => void;
  isPinned?: boolean;
  onTogglePin?: () => void;
}

export function PortfolioOutline({ onNavigate, isPinned = true, onTogglePin }: PortfolioOutlineProps) {
  const {
    draft,
    dispatch,
    selectedNode,
    setSelectedNode,
    userTier,
    onUpgradeClick,
  } = useProfileDraft();

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteTargetSection, setDeleteTargetSection] = useState<SectionData | null>(null);

  const sections = draft?.sections || [];
  const isPro = userTier === "pro" || userTier === "premium";
  const isSectionLimitReached = !isPro && sections.length >= 5;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragId(null);
    if (!over || active.id === over.id || !draft || !isPro) return;

    const sourceIndex = draft.sections.findIndex((s) => s.id === active.id);
    const destinationIndex = draft.sections.findIndex((s) => s.id === over.id);

    if (sourceIndex !== -1 && destinationIndex !== -1) {
      dispatch({
        type: "REORDER_SECTIONS",
        payload: { sourceIndex, destinationIndex },
      });
    }
  };

  const handleMoveArrow = useCallback(
    (index: number, direction: "up" | "down") => {
      if (!draft || !isPro) return;
      const destinationIndex = direction === "up" ? index - 1 : index + 1;
      if (destinationIndex < 0 || destinationIndex >= draft.sections.length) return;
      dispatch({
        type: "REORDER_SECTIONS",
        payload: { sourceIndex: index, destinationIndex },
      });
    },
    [draft, dispatch]
  );

  const handleDuplicateSection = useCallback(
    (sourceSection: SectionData) => {
      if (!isPro) {
        onUpgradeClick("section_reorder");
        return;
      }
      if (isSectionLimitReached) {
        onUpgradeClick("section_limit");
        return;
      }
      const sourceIndex = sections.findIndex((s) => s.id === sourceSection.id);
      const clonedSection: SectionData = {
        id: `tmp_${crypto.randomUUID()}`,
        clientKey: crypto.randomUUID(),
        type: sourceSection.type,
        pageId: sourceSection.pageId,
        visible: sourceSection.visible,
        displayOrder: sourceIndex + 1,
        content: JSON.parse(JSON.stringify(sourceSection.content || {})),
      };
      
      dispatch({
        type: "DUPLICATE_SECTION",
        payload: { sourceId: sourceSection.id, newSection: clonedSection },
      });
      
      setSelectedNode({ kind: "section", sectionId: clonedSection.id, clientKey: clonedSection.clientKey });
    },
    [isPro, isSectionLimitReached, onUpgradeClick, sections, dispatch, setSelectedNode]
  );

  const handleAddSection = (type: SectionType) => {
    const defaultPageId = sections[0]?.pageId;
    const newSection: SectionData = {
      id: `tmp_${crypto.randomUUID()}`,
      clientKey: crypto.randomUUID(),
      type,
      pageId: defaultPageId,
      displayOrder: sections.length,
      visible: true,
      content: createDefaultSectionContent(type),
    };
    dispatch({ type: "ADD_SECTION", payload: newSection });
    setSelectedNode({ kind: "section", sectionId: newSection.id, clientKey: newSection.clientKey });
    setAddModalOpen(false);
  };

  const handleDeleteSectionConfirm = () => {
    if (!deleteTargetSection || deleteTargetSection.type === "about") return;
    const targetId = deleteTargetSection.id;
    const targetIndex = sections.findIndex((s) => s.id === targetId);
    dispatch({ type: "DELETE_SECTION", payload: { sectionId: targetId } });

    // Try to focus adjacent node if the deleted one was selected
    if (selectedNode?.kind === "section" && selectedNode.sectionId === targetId) {
      const remaining = sections.filter((s) => s.id !== targetId);
      if (remaining.length > 0) {
        const nextTarget = targetIndex < remaining.length ? remaining[targetIndex] : remaining[remaining.length - 1];
        setSelectedNode({ kind: "section", sectionId: nextTarget.id, clientKey: nextTarget.clientKey });
      } else {
        setSelectedNode({ kind: "overview" });
      }
    }
    setDeleteTargetSection(null);
  };

  const draggedSection = activeDragId ? sections.find((s) => s.id === activeDragId) : null;
  const draggedMeta = draggedSection ? SECTION_METADATA[draggedSection.type as SectionType] : null;

  const NavItem = ({ icon: Icon, label, isActive, onClick }: { icon: any, label: string, isActive: boolean, onClick: () => void }) => (
    <button
      type="button"
      onClick={() => { onClick(); onNavigate?.(); }}
      className={`flex items-center w-full rounded-lg text-sm font-medium transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        isActive
          ? "bg-primary text-primary-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground hover:bg-muted"
      } ${
        !isPinned
          ? "h-10 px-0 group-hover/sidebar:px-2 gap-0 group-hover/sidebar:gap-2.5 justify-center group-hover/sidebar:justify-start"
          : "px-2.5 py-2 gap-2.5 justify-start"
      }`}
      title={!isPinned ? label : undefined}
    >
      <div className={`flex items-center justify-center shrink-0 ${!isPinned ? "w-10 h-10 group-hover/sidebar:w-5 group-hover/sidebar:h-5" : "w-5 h-5"}`}>
        <Icon className="h-4 w-4 shrink-0" />
      </div>
      <span className={`truncate text-left transition-all duration-200 ${!isPinned ? 'opacity-0 w-0 overflow-hidden group-hover/sidebar:opacity-100 group-hover/sidebar:w-auto' : 'opacity-100'}`}>{label}</span>
    </button>
  );

  return (
    <div className="flex flex-col h-full bg-card overflow-y-auto select-none border-r border-border">
      {/* Brand */}
      <div className={`px-2 py-3 border-b border-border flex items-center justify-between shrink-0 transition-all duration-300 ${!isPinned ? 'flex-col group-hover/sidebar:flex-row group-hover/sidebar:px-4' : 'px-4 gap-3'}`}>
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground font-black text-sm flex items-center justify-center shadow-sm shrink-0">
            P
          </div>
          <span className={`font-semibold text-foreground tracking-tight transition-all duration-200 ${!isPinned ? 'hidden group-hover/sidebar:block' : 'block'}`}>Profolio</span>
        </div>
        {onTogglePin && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onTogglePin}
            className={`h-7 w-7 p-0 text-muted-foreground hover:text-foreground transition-all duration-200 cursor-pointer ${!isPinned ? 'opacity-0 group-hover/sidebar:opacity-100 absolute right-3' : ''}`}
            title={isPinned ? "Collapse sidebar (unpin)" : "Pin sidebar (keep open)"}
          >
            {isPinned ? (
              <PanelLeftClose className="h-4 w-4" />
            ) : (
              <PanelLeftOpen className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-3 space-y-6">
        
        {/* Workspace Nodes */}
        <div className="px-2 space-y-1">
          <div className={`flex items-center px-1 mb-1 transition-all duration-200 ${!isPinned ? 'opacity-0 h-0 overflow-hidden group-hover/sidebar:opacity-100 group-hover/sidebar:h-auto' : 'opacity-100'}`}>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Workspace
            </span>
          </div>
          <NavItem
            icon={LayoutTemplate}
            label="Overview"
            isActive={selectedNode?.kind === "overview"}
            onClick={() => setSelectedNode({ kind: "overview" })}
          />
          <NavItem
            icon={Layers}
            label="Pages"
            isActive={selectedNode?.kind === "pages"}
            onClick={() => setSelectedNode({ kind: "pages" })}
          />
        </div>

        {/* Global Design Nodes */}
        <div className="px-2 space-y-1">
          <div className={`flex items-center px-1 mb-1 transition-all duration-200 ${!isPinned ? 'opacity-0 h-0 overflow-hidden group-hover/sidebar:opacity-100 group-hover/sidebar:h-auto' : 'opacity-100'}`}>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Global
            </span>
          </div>
          <NavItem 
            icon={Palette} 
            label="Site Style" 
            isActive={selectedNode?.kind === "siteStyle"} 
            onClick={() => setSelectedNode({ kind: "siteStyle" })} 
          />
          <NavItem 
            icon={Type} 
            label="Header" 
            isActive={selectedNode?.kind === "header"} 
            onClick={() => setSelectedNode({ kind: "header" })} 
          />
          <NavItem 
            icon={Sparkles} 
            label="Effects" 
            isActive={selectedNode?.kind === "effects"} 
            onClick={() => setSelectedNode({ kind: "effects" })} 
          />
          <NavItem 
            icon={PanelBottom} 
            label="Footer" 
            isActive={selectedNode?.kind === "footer"} 
            onClick={() => setSelectedNode({ kind: "footer" })} 
          />
          <NavItem 
            icon={Sliders} 
            label="Advanced" 
            isActive={selectedNode?.kind === "advanced"} 
            onClick={() => setSelectedNode({ kind: "advanced" })} 
          />
        </div>

        {/* Sections */}
        <div className="px-2 space-y-1">
          <div className={`flex items-center justify-between px-1 transition-all duration-200 ${!isPinned ? 'opacity-0 h-0 overflow-hidden group-hover/sidebar:opacity-100 group-hover/sidebar:h-auto group-hover/sidebar:mb-1' : 'mb-1'}`}>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Sections
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground cursor-pointer"
              title="Add new section"
              onClick={() => {
                if (isSectionLimitReached) {
                  onUpgradeClick("custom_sections");
                } else {
                  setAddModalOpen(true);
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sections.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((section, index) => {
                const meta = SECTION_METADATA[section.type as SectionType];
                const baseLabel = meta?.label || section.type;
                const isActive = selectedNode?.kind === "section" && selectedNode.sectionId === section.id;

                return (
                  <SortableSectionItem
                    key={section.clientKey || section.id}
                    id={section.id}
                    label={baseLabel}
                    icon={meta?.icon || FileText}
                    visible={section.visible}
                    isActive={isActive}
                    itemCount={getSectionItemCount(section)}
                    onSelect={() => { setSelectedNode({ kind: "section", sectionId: section.id, clientKey: section.clientKey }); onNavigate?.(); }}
                    onToggleVisibility={() =>
                      dispatch({
                        type: "UPDATE_SECTION_VISIBILITY",
                        payload: { sectionId: section.id, visible: !section.visible },
                      })
                    }
                    isLast={index === sections.length - 1}
                    onMoveUp={() => handleMoveArrow(index, "up")}
                    onMoveDown={() => handleMoveArrow(index, "down")}
                    onDuplicate={() => handleDuplicateSection(section as SectionData)}
                    onDelete={
                      section.type !== "about"
                        ? () => setDeleteTargetSection(section as SectionData)
                        : undefined
                    }
                    disabled={!isPro}
                    isPinned={isPinned}
                  />
                );
              })}
            </SortableContext>
            <DragOverlay>
              {draggedSection && draggedMeta ? (
                <SectionDragOverlayItem
                  label={draggedMeta.label}
                  icon={draggedMeta.icon}
                  itemCount={getSectionItemCount(draggedSection)}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      <AddSectionModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onSelectType={handleAddSection}
        existingTypes={sections.map((s) => s.type as SectionType)}
      />

      <DeleteSectionDialog
        open={Boolean(deleteTargetSection)}
        onOpenChange={(open) => !open && setDeleteTargetSection(null)}
        sectionLabel={
          deleteTargetSection
            ? SECTION_METADATA[deleteTargetSection.type as SectionType]?.label ||
              deleteTargetSection.type
            : ""
        }
        onConfirm={handleDeleteSectionConfirm}
      />

      {/* Rail Footer: Pin / Collapse Toggle */}
      {onTogglePin && (
        <div className="p-2 border-t border-border shrink-0 flex items-center justify-between">
          <button
            type="button"
            onClick={onTogglePin}
            className={`flex items-center gap-2.5 w-full p-2 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              !isPinned ? 'justify-center group-hover/sidebar:justify-start' : ''
            }`}
            title={isPinned ? "Collapse sidebar" : "Pin sidebar open"}
          >
            {isPinned ? (
              <>
                <PanelLeftClose className="h-4 w-4 shrink-0" />
                <span className="truncate">Collapse sidebar</span>
              </>
            ) : (
              <>
                <PanelLeftOpen className="h-4 w-4 shrink-0" />
                <span className="truncate opacity-0 w-0 overflow-hidden group-hover/sidebar:opacity-100 group-hover/sidebar:w-auto transition-all duration-200">
                  Pin sidebar
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
